import { hexToRgb, readableTextColor } from "@u/contrast";

interface BrandProps {
  bgColor: string;
  logoUrl?: string | null;
  name?: string;
  /** Render the logo flat (no circle crop / background) instead of a circular avatar. */
  logoNoBg?: boolean;
  /** Brand-name text color. Unset ⇒ auto-contrast against `bgColor`. */
  textColor?: string;
  /** Shimmer-bar color. Unset ⇒ same as the (auto-contrast) text color. */
  barColor?: string;
}

const rgba = (hex: string, a: number) => {
  const [r, g, b] = hexToRgb(hex) ?? [255, 255, 255];
  return `rgba(${r},${g},${b},${a})`;
};

interface LoadingScreenProps {
  fullScreen?: boolean;
  tap?: BrandProps;
  whiteLabelBrand?: BrandProps;
}

/** Owner-branded splash. `contained` renders it to fill a framed box (for the
 *  editor preview) instead of the full viewport. */
export function BrandLoadingScreen({
  brand,
  poweredBy,
  contained,
}: {
  brand: BrandProps;
  poweredBy?: boolean;
  contained?: boolean;
}) {
  const textColor = brand.textColor || readableTextColor(brand.bgColor);
  const barColor = brand.barColor || textColor;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative flex flex-col items-center justify-between w-full px-8 ${
        contained ? "h-full py-10" : "min-h-dvh py-16"
      }`}
      style={{
        backgroundColor: brand.bgColor,
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      <span className="sr-only">Loading…</span>
      <style>{`
        @keyframes andar-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes andar-pulse {
          0%, 100% { opacity: 0.75; }
          50%      { opacity: 1; }
        }
        .andar-shimmer { animation: andar-shimmer 1.8s ease-in-out infinite; }
        .andar-pulse   { animation: andar-pulse   2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .andar-shimmer, .andar-pulse { animation: none; }
        }
      `}</style>

      <div className="flex-1" />
      <div className="flex flex-col items-center justify-center">
        {brand.logoUrl ? (
          <div className="flex flex-col items-center andar-pulse">
            {brand.logoNoBg ? (
              <img
                src={brand.logoUrl}
                alt={brand.name ?? ""}
                className="w-28 h-28 object-contain drop-shadow-xl shrink-0"
              />
            ) : (
              <div className="w-28 h-28 rounded-full overflow-hidden drop-shadow-xl shrink-0">
                <img
                  src={brand.logoUrl}
                  alt={brand.name ?? ""}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {brand.name && (
              <h1
                className="text-2xl tracking-wide text-center mt-4"
                style={{ color: textColor }}
              >
                {brand.name}
              </h1>
            )}
          </div>
        ) : brand.name ? (
          <h1
            className="text-4xl tracking-wide text-center andar-pulse"
            style={{ color: textColor }}
          >
            {brand.name}
          </h1>
        ) : (
          <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        )}
      </div>
      <div className="flex-1" />
      {poweredBy && (
        <p
          className="text-center text-xs tracking-widest uppercase"
          style={{ color: rgba(textColor, 0.3) }}
        >
          by Andar Labs
        </p>
      )}

      <div
        className="absolute bottom-0 left-0 w-full h-0.5 overflow-hidden"
        style={{ backgroundColor: rgba(barColor, 0.12) }}
      >
        <div
          className="andar-shimmer h-full w-1/3"
          style={{
            background: `linear-gradient(90deg, transparent, ${rgba(barColor, 0.85)}, transparent)`,
          }}
        />
      </div>
    </div>
  );
}

export default function LoadingScreen({
  fullScreen = true,
  tap,
  whiteLabelBrand,
}: LoadingScreenProps) {
  if (whiteLabelBrand) {
    return <BrandLoadingScreen brand={whiteLabelBrand} poweredBy={false} />;
  }

  if (tap) {
    return <BrandLoadingScreen brand={tap} poweredBy />;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center justify-center overflow-hidden ${
        fullScreen
          ? "fixed inset-0 z-9999 h-dvh w-full bg-background"
          : "h-full w-full py-24"
      }`}
    >
      <span className="sr-only">Loading…</span>
      <style>{`
        @keyframes andar-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.08); }
        }
        @keyframes andar-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .andar-pulse   { animation: andar-pulse   2.4s ease-in-out infinite; }
        .andar-shimmer { animation: andar-shimmer 1.8s ease-in-out infinite; }
      `}</style>

      {/* light mode icon */}
      <img
        src="/logos/icon-color.svg"
        alt=""
        aria-hidden="true"
        className="andar-pulse w-20 h-20 object-contain dark:hidden"
      />
      {/* dark mode icon */}
      <img
        src="/logos/icon-white.svg"
        alt=""
        aria-hidden="true"
        className="andar-pulse w-20 h-20 object-contain hidden dark:block"
      />

      {fullScreen && (
        <div className="absolute bottom-0 left-0 w-full h-0.5 overflow-hidden bg-primary/8">
          <div className="andar-shimmer h-full w-1/3 bg-linear-to-r from-transparent via-primary to-transparent" />
        </div>
      )}
    </div>
  );
}
